<template>
  <v-main>
    <div class="title-background">
      <TitleHeader :title="t('thanks.subtitle')" :subtitle="t('thanks.title')" />
    </div>
    <v-container class="pb-0">
      <v-row class="mt-3" no-gutters>
        <v-col cols="12" class="mb-3">
          <ReturnLink :label="t('familiesList.returnToMainPage')" to-name="home" />
        </v-col>
      </v-row>
      <v-row no-gutters class="mt-3 sz-primary-background pa-4 pa-sm-5">
        <v-col cols="12" class="sz-primary-grey--text">
          <h1 class="f-24 font-weight-medium">
            <span v-html="t('thanks.header')"></span>
          </h1>
          <div class="border-bottom mb-5" />
        </v-col>
        <v-col cols="12">
          <v-row no-gutters class="flex-nowrap">
            <v-col cols="1" sm="auto">
              <v-icon :size="25" color="szLightGrey" class="mt-1"> $email </v-icon>
            </v-col>
            <v-col cols="11" sm="auto" class="sz-primary-grey--text font-weight-regular pl-3">
              <p class="f-16 mb-5">
                {{ t('thanks.emailInfo') }}
              </p>
              <div>
                <p class="f-16 mb-0">
                  <a class="f-16 primary-link pointer" :href="familyUrlChosen" target="_blank">
                    {{ t('thanks.familyLink.1') }}
                  </a>
                  {{ t('thanks.familyLink.2') }}
                </p>
                <input id="textToCopy" type="hidden" :value="familyUrl" />
              </div>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12">
          <v-row no-gutters class="flex-nowrap mt-5">
            <v-col cols="1" sm="auto">
              <v-icon :size="25" color="szLightGrey" class="mt-1"> $chat </v-icon>
            </v-col>
            <v-col cols="11" sm="auto" class="sz-primary-grey--text font-weight-regular pl-3">
              <p class="f-16 mb-1">
                {{ t('thanks.volunteerInfo') }}
              </p>
              <p class="f-14 mb-0">
                <span class="f-20 mr-3">{{ callCenter.phone }}</span>
                <span v-if="callCenter.text">
                  {{ callCenter.text }}
                </span>
              </p>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>
    <v-container class="py-0">
      <v-row no-gutters>
        <v-col cols="12">
          <DonorSteps
            :title="t('home.thanksSteps.title')"
            :steps="tm('home.thanksSteps.steps')"
            :guide-button-text="t('label.showGuide')"
            :center-steps-text="false"
            plain-tiles
            class="thanks-page__donor-steps"
          />
        </v-col>
      </v-row>
    </v-container>

    <InfoSectionThanks />

    <v-container>
      <v-row>
        <v-col cols="12">
          <FAQPanels :faq="THANKS_PAGE_FAQ">
            <div class="mt-6 d-flex justify-center">
              <BaseButton :href="LINKS.guide" variant="secondary" arrow>
                {{ t('label.showGuide') }}
              </BaseButton>
            </div>
          </FAQPanels>
        </v-col>
        <v-col cols="12">
          <BannerBox v-bind="tm('banners.APThanksPage')" />
        </v-col>
      </v-row>
    </v-container>
    <FooterComponent />
  </v-main>
</template>

<script setup lang="ts">
// Vue
import { computed } from 'vue'

// Local Components
import BannerBox from '@/components/widgets/BannerBox/BannerBox.vue'
import BaseButton from '@/components/widgets/BaseButton.vue'
import DonorSteps from '@/components/widgets/DonorSteps.vue'
import FAQPanels from '@/components/widgets/FAQPanels.vue'
import FooterComponent from '@/components/widgets/FooterComponent.vue'
import ReturnLink from '@/components/widgets/ReturnLink.vue'
import TitleHeader from '@/components/widgets/TitleHeader.vue'

// Constants
import { LINKS } from '@/constants/externalLinks'
import { THANKS_PAGE_FAQ } from '@/constants/faq'

// Utilities
import InfoSectionThanks from '@/components/Home/InfoSection/InfoSectionThanks.vue'
import { useDonorStore } from '@/stores/Donor'
import getLangsVariable from '@/utils/getLangsVariable'
import { useI18n } from 'vue-i18n'

const { t, tm } = useI18n()

const donorStore = useDonorStore()
const donor = computed(() => donorStore.donor)

// Construct the family URL
const familyUrl = computed<string>(() => {
  if (!donor.value) return ''
  const { familyEditionId, warehouseId } = donor.value

  const url = new URL(window.location.href)
  const PUBLIC_PATH = import.meta.env.VITE_PUBLIC_PATH ?? '/'
  url.pathname = `${PUBLIC_PATH}rodzina/${familyEditionId}/${warehouseId}`

  return url.href
})

const familyUrlChosen = computed<string>(() => `${familyUrl.value}?context=family_choosen`)

const callCenter = {
  phone: getLangsVariable(['OTHER', 'callCenter', 'phone']) || t('footer.callCenter.phone'),
  text: getLangsVariable(['OTHER', 'callCenter', 'text']) || t('footer.callCenter.text')
}
</script>

<style lang="scss">
@use '@/assets/css/variables.scss' as variables;
@use 'sass:map';

.thanks-page__donor-steps {
  // The narrow columns only from `sm` up - on mobile `DonorSteps` gives the text the full width
  .step {
    @media (min-width: map.get(variables.$grid-breakpoints, 'sm')) {
      width: 260px;
    }

    @media (min-width: map.get(variables.$grid-breakpoints, 'lg')) {
      width: 200px;
    }
  }
}
</style>
